import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard, FileSpreadsheet, GraduationCap, Users,
    UserCheck, School, Search, LogOut, Printer, MessageSquare,
    Menu, X,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
    { to: "/admin", end: true, label: "لوحة التحليلات", icon: LayoutDashboard, testid: "nav-overview" },
    { to: "/admin/import", label: "استيراد ملفات Excel", icon: FileSpreadsheet, testid: "nav-import" },
    { to: "/admin/students", label: "حسابات الطالبات", icon: GraduationCap, testid: "nav-students" },
    { to: "/admin/parents", label: "حسابات أولياء الأمور", icon: Users, testid: "nav-parents" },
    { to: "/admin/trainers", label: "المدربات", icon: UserCheck, testid: "nav-trainers" },
    { to: "/admin/classes", label: "الفصول", icon: School, testid: "nav-classes" },
    { to: "/admin/search", label: "البحث الشامل", icon: Search, testid: "nav-search" },
    { to: "/admin/messages", label: "مراقبة الرسائل", icon: MessageSquare, testid: "nav-messages" },
    { to: "/admin/report", label: "الطباعة والتقارير", icon: Printer, testid: "nav-report" },
];

export default function AdminLayout() {
    const { user, logout, school } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);

    // Auto-close on route change (mobile)
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Lock body scroll while mobile drawer is open
    useEffect(() => {
        if (open) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = prev; };
        }
    }, [open]);

    const doLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50 lg:flex">
            {/* Backdrop (mobile only) */}
            <div
                onClick={() => setOpen(false)}
                aria-hidden="true"
                className={`lg:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-sm transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            />

            {/* Sidebar */}
            <aside
                data-testid="admin-sidebar"
                className={`no-print w-72 shrink-0 bg-[#0F172A] text-white flex flex-col
                    fixed inset-y-0 right-0 z-40 transition-transform duration-300 ease-in-out
                    ${open ? "translate-x-0" : "translate-x-full"}
                    lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:z-0`}
            >
                <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between gap-2">
                    <BrandLogo size={44} />
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        aria-label="إغلاق القائمة"
                        data-testid="btn-close-sidebar"
                        className="lg:hidden p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                                `relative flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all ${
                                    isActive
                                        ? "bg-white/10 text-white side-active-pill"
                                        : "text-white/70 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <n.icon className="w-4 h-4 shrink-0" />
                            <span>{n.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-white/10 p-4 space-y-3">
                    <div className="text-xs text-white/60">
                        <div className="text-white/90 font-medium">{user?.full_name}</div>
                        <div className="font-mono-num">{user?.username}</div>
                    </div>
                    <Button
                        variant="secondary"
                        size="sm"
                        className="w-full bg-white/10 hover:bg-white/20 text-white border-white/10"
                        onClick={doLogout}
                        data-testid="btn-logout"
                    >
                        <LogOut className="w-4 h-4 ml-2" />
                        تسجيل الخروج
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0">
                <header className="no-print sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 lg:py-4 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-label="فتح القائمة"
                        data-testid="btn-open-sidebar"
                        className="lg:hidden p-2 rounded-lg text-[#1B2A4E] hover:bg-slate-100 transition-colors -mr-1"
                    >
                        <Menu className="w-6 h-6" />
                    </button>
                    <div className="text-xs sm:text-sm text-slate-500 flex-1 min-w-0 truncate">
                        <span className="text-[#1B2A4E] font-semibold">{school?.app_name}</span>
                        <span className="mx-2 text-slate-300 hidden sm:inline">/</span>
                        <span className="hidden sm:inline">{school?.name}</span>
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap">
                        {new Date().toLocaleDateString("ar-SA-u-nu-latn")}
                    </div>
                </header>
                <div className="p-4 sm:p-6 lg:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
